/**
 * @fileoverview Componente selector de tipos de templates de WhatsApp con iconos y renderizado optimizado
 * @module WsTemplateTypeSelect
 * @category Component - Molecule
 * @since 1.0.0
 * @version 2.1.0
 */

import React, { useCallback, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { templateIcon } from '../../util/templateIconType';
import DropdownMC from './DropdownMC';

/**
 * @typedef {Object} TemplateTypeOption
 * @description Estructura de datos para las opciones de tipos de templates de WhatsApp
 * @property {string|number} id - Identificador único del tipo de template
 * @property {string|number} value - Valor numérico del tipo (0-3) o 'all' para todos
 * @property {string} label - Texto descriptivo del tipo de template
 * @property {string} type - Tipo de template ('all', 'text', 'image', 'document', 'video')
 * @property {string} icon - Clase CSS del icono asociado al tipo de template
 */

/**
 * Construye un objeto de opción de tipo de template con icono asociado.
 * 
 * Función utilitaria que crea la estructura de datos necesaria para cada
 * opción del dropdown, incluyendo la resolución del icono correspondiente.
 * 
 * @function buildTypeOption
 * @category Utility Function
 * 
 * @description
 * **Flujo de Construcción:**
 * 1. **Creación de Base**: Estructura básica con id, value, label, type
 * 2. **Resolución de Icono**: Llamada a templateIcon() para obtener clase CSS
 * 3. **Objeto Resultante**: Retorna objeto completo para uso en dropdown
 * 
 * **Mapeo de Tipos:**
 * - 'all' → Opción especial "Todos" con icono genérico
 * - 'text' → Templates de texto con icono de texto
 * - 'image' → Templates de imagen con icono de imagen
 * - 'document' → Templates de documento con icono de archivo
 * - 'video' → Templates de video con icono de video
 * 
 * @param {string} type - Tipo de template ('all', 'text', 'image', 'document', 'video')
 * @param {string|number} value - Valor numérico del tipo o 'all'
 * @param {string} text - Texto descriptivo para mostrar en la opción
 * 
 * @returns {TemplateTypeOption} Objeto de opción completamente configurado
 * 
 * @example
 * ```javascript
 * // Crear opción para templates de texto
 * const textOption = buildTypeOption('text', 0, 'Texto');
 * // Resultado: { id: 0, value: 0, label: 'Texto', type: 'text', icon: 'mi-text-format' }
 * ```
 * 
 * @example
 * ```javascript
 * // Crear opción especial "Todos"
 * const allOption = buildTypeOption('all', 'all', 'Todos');
 * // Resultado: { id: 'all', value: 'all', label: 'Todos', type: 'all', icon: 'mi-select-all' }
 * ```
 */

const buildTypeOption = (type, value, text) => ({
  id: value,
  value,
  label: text,
  type,
  icon: templateIcon(type)
});

/**
 * Construye el array de opciones de tipos de templates usando traducciones.
 * 
 * @function getMessageTypeOptions
 * @param {Function} t - Función de traducción de i18next
 * @returns {TemplateTypeOption[]} Array de opciones traducidas
 */
const getMessageTypeOptions = (t) => [
  buildTypeOption('all', 'all', t('options.all')),
  buildTypeOption('text', 0, t('options.text')),
  buildTypeOption('image', 1, t('options.image')),
  buildTypeOption('document', 3, t('options.document')),
  buildTypeOption('video', 2, t('options.video'))
];

/**
 * Componente Molecule selector especializado para tipos de templates de WhatsApp.
 * 
 * Proporciona un dropdown optimizado para seleccionar tipos de templates con
 * iconos visuales representativos y renderizado personalizado para cada tipo.
 * 
 * @component WsTemplateTypeSelect
 * @category Component - Molecule
 * 
 * @description
 * **Arquitectura Optimizada (v2.1.0):**
 * - ✅ **Performance**: React.memo, useCallback, useMemo para optimización completa
 * - ✅ **Static Data**: Opciones precalculadas y memoizadas fuera del componente
 * - ✅ **Icon Integration**: Integración con templateIcon() para iconos dinámicos
 * - ✅ **Visual Design**: Renderizado personalizado con iconos y estilos consistentes
 * - ✅ **Form Integration**: Compatible con sistemas de formularios estándar
 * - ✅ **WhatsApp Specific**: Especializado para tipos de templates de WhatsApp Business
 * 
 * **Características:**
 * - Selección de tipos: Texto, Imagen, Video, Documento, y opción "Todos"
 * - Iconos descriptivos para cada tipo de template
 * - Renderizado optimizado con funciones memoizadas
 * - Estilo visual consistente con color de iconos unificado (#808191)
 * - Integración completa con DropdownMC como componente base
 * - Soporte para labels flotantes y configuraciones personalizadas
 * - Datos estáticos optimizados para máximo rendimiento
 * 
 * **Atomic Design:**
 * - **Nivel**: Molecule
 * - **Composición**: Utiliza DropdownMC como organismo base
 * - **Dependencias**: templateIcon utility, DropdownMC component
 * 
 * **Props Interface:**
 * ```javascript
 * {
 *   defaultValue?: string|number,     // Valor inicial del tipo seleccionado
 *   onSelect?: Function,              // Callback al seleccionar tipo
 *   name?: string,                    // Nombre del campo (default: 'template_type')
 *   placeholder?: string,             // Texto placeholder (default: 'Selecciona tipo')
 *   className?: string,               // Clases CSS adicionales
 *   useFloatingLabel?: boolean,       // Estilo de label flotante
 *   label?: string                    // Etiqueta del campo (default: 'Tipo')
 * }
 * ```
 * 
 * **Flujo de Operación:**
 * 1. **Inicialización**: Configuración de estados y hooks de optimización
 * 2. **Renderizado**: Mostrar dropdown con opciones estáticas de tipos
 * 3. **Selección**: Manejar selección y ejecutar callback onSelect
 * 4. **Visualización**: Mostrar opción seleccionada con icono correspondiente
 * 
 * **Tipos de Templates Soportados:**
 * - **Todos** ('all'): Vista general de todos los tipos
 * - **Texto** (0): Templates de solo texto
 * - **Imagen** (1): Templates con imagen y texto opcional
 * - **Video** (2): Templates con video y texto opcional
 * - **Documento** (3): Templates con archivo adjunto
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string|number} [props.defaultValue] - Valor inicial del tipo preseleccionado
 * @param {Function} [props.onSelect] - Función callback al seleccionar un tipo
 * @param {string} [props.name='template_type'] - Nombre del campo para formularios
 * @param {string} [props.placeholder='Selecciona tipo'] - Texto placeholder del dropdown
 * @param {string} [props.className=''] - Clases CSS adicionales para el contenedor
 * @param {boolean} [props.useFloatingLabel=false] - Activar estilo de label flotante
 * @param {string} [props.label='Tipo'] - Etiqueta de texto para el campo
 * 
 * @returns {JSX.Element} Elemento JSX del selector de tipos de templates renderizado
 * 
 * @example
 * ```jsx
 * // Uso básico para filtrado de templates
 * <WsTemplateTypeSelect
 *   onSelect={(typeValue) => {
 *     console.log('Tipo seleccionado:', typeValue);
 *     filterTemplatesByType(typeValue);
 *   }}
 *   placeholder="Filtrar por tipo"
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Uso avanzado con valor inicial y label flotante
 * <WsTemplateTypeSelect
 *   name="template_message_type"
 *   defaultValue={1} // Preseleccionar "Imagen"
 *   label="Tipo de Template"
 *   useFloatingLabel={true}
 *   onSelect={(typeValue) => {
 *     if (typeValue === 'all') {
 *       loadAllTemplates();
 *     } else {
 *       loadTemplatesByType(typeValue);
 *     }
 *   }}
 *   className="tw-mb-4"
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Integración en formulario de creación de templates
 * function TemplateCreationForm() {
 *   const [templateType, setTemplateType] = useState(0);
 *   
 *   return (
 *     <form>
 *       <WsTemplateTypeSelect
 *         name="type"
 *         defaultValue={templateType}
 *         onSelect={(type) => {
 *           setTemplateType(type);
 *           updateTemplateForm({ type });
 *         }}
 *         label="Tipo de Contenido"
 *       />
 *     </form>
 *   );
 * }
 * ```
 * 
 * @see {@link DropdownMC} Componente base utilizado para el dropdown
 * @see {@link templateIcon} Función utilitaria para obtener iconos de tipos
 * 
 * @since 1.0.0
 * @version 2.1.0 - Optimización completa con React.memo y hooks memoizados
 */
const WsTemplateTypeSelect = memo(({
  defaultValue,
  onSelect,
  name = 'template_type',
  placeholder,
  className = '',
  useFloatingLabel = false,
  label
}) => {
  const { t } = useTranslation('WsTemplateTypeSelectMC');
  
  // Usar traducciones como valores por defecto
  const defaultPlaceholder = placeholder || t('placeholder');
  const defaultLabel = label || t('label');
  
  // Generar opciones traducidas
  const messageTypeOptions = useMemo(() => getMessageTypeOptions(t), [t]);

  /**
   * Objeto de estilo memoizado para los iconos de tipos de templates.
   * 
   * Define el color unificado para todos los iconos del selector,
   * optimizado para evitar recreación innecesaria del objeto.
   * 
   * @constant iconStyle
   * @memberof WsTemplateTypeSelect
   * @private
   * 
   * @description
   * **Configuración:**
   * - **Color**: #808191 (gris medio) para consistencia visual
   * - **Aplicación**: Usado en iconos de opciones y valor seleccionado
   * - **Optimización**: Memoizado sin dependencias para máxima eficiencia
   * 
   * **Beneficios:**
   * - Evita recreación del objeto en cada render
   * - Mantiene referencia estable para funciones dependientes
   * - Asegura color consistente en todo el componente
   * 
   * @type {Object} Objeto CSS con propiedad color
   * @property {string} color - Color hexadecimal #808191 para los iconos
   * 
   * @example
   * ```javascript
   * // Aplicación en elemento JSX
   * <i className="mi-image tw-text-lg" style={iconStyle} />
   * // Resultado: icono con color #808191
   * ```
   */
  const iconStyle = useMemo(() => ({ color: '#808191' }), []);

  /**
   * Renderiza una opción individual de tipo de template en la lista desplegable.
   * 
   * Proporciona el layout personalizado para mostrar cada tipo con su icono
   * representativo y texto descriptivo con truncado automático.
   * 
   * @function renderTypeOption
   * @memberof WsTemplateTypeSelect
   * @private
   * 
   * @description
   * **Elementos Renderizados:**
   * - **Icono**: Elemento i con clase dinámica según el tipo
   * - **Texto**: Span con label del tipo y truncado para overflow
   * 
   * **Lógica de Iconos:**
   * - **Tipo 'all'**: Usa icono fijo 'mi-menu-fill'
   * - **Otros tipos**: Usa option.icon obtenido de templateIcon()
   * 
   * **Layout:**
   * ```
   * [📄] Tipo de Template...
   * icono  texto (truncado)
   * ```
   * 
   * **Estilos TailwindCSS:**
   * - Contenedor: flex, items-center, w-full
   * - Icono: mr-3, flex-shrink-0, text-lg
   * - Texto: text-sm, font-medium, text-gray-900, truncate
   * 
   * **Optimización:**
   * - Memoizada con useCallback y dependencia en iconStyle
   * - Evita recreación cuando iconStyle no cambia
   * 
   * @param {TemplateTypeOption} option - Objeto de opción de tipo de template
   * @param {string|number} option.id - ID del tipo ('all', 0, 1, 2, 3)
   * @param {string} option.label - Texto descriptivo del tipo
   * @param {string} option.type - Categoría del tipo ('all', 'text', 'image', etc.)
   * @param {string} option.icon - Clase CSS del icono asociado
   * 
   * @returns {JSX.Element} Elemento JSX con el contenido renderizado de la opción
   * 
   * @example
   * ```javascript
   * // Opción de template de imagen
   * const imageOption = {
   *   id: 1,
   *   label: "Imagen",
   *   type: "image",
   *   icon: "mi-image"
   * };
   * const rendered = renderTypeOption(imageOption);
   * // Resultado: [🇮] Imagen
   * ```
   * 
   * @example
   * ```javascript
   * // Opción especial "Todos"
   * const allOption = {
   *   id: 'all',
   *   label: "Todos",
   *   type: "all",
   *   icon: "mi-select-all"
   * };
   * const rendered = renderTypeOption(allOption);
   * // Resultado: [📝] Todos (con icono mi-menu-fill)
   * ```
   */
  const renderTypeOption = useCallback((option) => (
    <div className="tw-flex tw-items-center tw-w-full">
      <div className="tw-mr-3 tw-flex-shrink-0">
        <i
          className={`${option.type === 'all' ? 'mi-menu-fill' : option.icon} tw-text-lg`}
          style={iconStyle}
        />
      </div>
      <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
        {option.label}
      </span>
    </div>
  ), [iconStyle]);

  /**
   * Renderiza el tipo de template seleccionado en el botón principal del dropdown.
   * 
   * Proporciona el layout para mostrar el tipo actualmente seleccionado con
   * icono representativo y fallback al placeholder si no hay selección.
   * 
   * @function renderSelectedType
   * @memberof WsTemplateTypeSelect
   * @private
   * 
   * @description
   * **Elementos Renderizados:**
   * - **Con Selección**: Icono + texto del tipo seleccionado
   * - **Sin Selección**: Solo placeholder en gris
   * 
   * **Lógica Condicional:**
   * ```
   * option ? (
   *   [📄] Tipo Seleccionado
   * ) : (
   *   Placeholder text (gris)
   * )
   * ```
   * 
   * **Comportamiento de Fallback:**
   * - Si option existe: muestra icono + option.label
   * - Si option es null/undefined: muestra placeholder estándar
   * - Icono usa misma lógica que renderTypeOption
   * 
   * **Estilos Diferenciados:**
   * - **Con selección**: text-gray-900 (negro)
   * - **Sin selección**: text-gray-500 (gris)
   * 
   * **Optimización:**
   * - Memoizada con useCallback
   * - Dependencias: iconStyle y placeholder
   * - Evita re-renders innecesarios del botón principal
   * 
   * @param {TemplateTypeOption|null|undefined} option - Tipo seleccionado o null
   * @param {string|number} [option.id] - ID del tipo seleccionado
   * @param {string} [option.label] - Texto del tipo seleccionado
   * @param {string} [option.type] - Categoría del tipo seleccionado
   * @param {string} [option.icon] - Clase CSS del icono del tipo
   * 
   * @returns {JSX.Element} Elemento JSX con el valor seleccionado renderizado
   * 
   * @example
   * ```javascript
   * // Con tipo de video seleccionado
   * const selectedOption = {
   *   id: 2,
   *   label: "Video",
   *   type: "video",
   *   icon: "mi-videocam"
   * };
   * const rendered = renderSelectedType(selectedOption);
   * // Resultado: [🎥] Video
   * ```
   * 
   * @example
   * ```javascript
   * // Sin tipo seleccionado
   * const rendered = renderSelectedType(null);
   * // Resultado: "Selecciona tipo" (en gris, sin icono)
   * ```
   * 
   * @example
   * ```javascript
   * // Opción "Todos" seleccionada
   * const allSelected = {
   *   id: 'all',
   *   label: "Todos",
   *   type: "all",
   *   icon: "mi-select-all"
   * };
   * const rendered = renderSelectedType(allSelected);
   * // Resultado: [📝] Todos (con icono mi-menu-fill)
   * ```
   */
  const renderSelectedType = useCallback((option) => (
    <div className="tw-flex tw-items-center tw-w-full">
      {option && (
        <>
          <div className="tw-mr-3 tw-flex-shrink-0">
            <i
              className={`${option.type === 'all' ? 'mi-menu-fill' : option.icon} tw-text-lg`}
              style={iconStyle}
            />
          </div>
          <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
            {option.label}
          </span>
        </>
      )}
      {!option && (
        <span className="tw-text-sm tw-text-gray-500">{defaultPlaceholder}</span>
      )}
    </div>
  ), [iconStyle, defaultPlaceholder]);

  /**
   * Objeto memoizado con todas las propiedades para el componente DropdownMC.
   * 
   * Optimiza el rendimiento al evitar la recreación innecesaria del objeto de props
   * y previene re-renders del DropdownMC cuando las dependencias no cambian.
   * 
   * @constant dropdownProps
   * @memberof WsTemplateTypeSelect
   * @private
   * 
   * @description
   * **Propiedades Incluidas:**
   * - **id**: Identificador único "template-type-select"
   * - **name**: Nombre del campo para formularios (default: 'template_type')
   * - **label**: Etiqueta del campo (default: 'Tipo')
   * - **options**: Array estático messageTypeOptions
   * - **defaultValue**: Valor del tipo seleccionado inicialmente
   * - **onSelect**: Handler callback para selección de tipos
   * - **placeholder**: Texto placeholder (default: 'Selecciona tipo')
   * - **className**: Clases CSS adicionales
   * - **useFloatingLabel**: Configuración de label flotante
   * - **renderOption**: Función memoizada renderTypeOption
   * - **renderSelected**: Función memoizada renderSelectedType
   * - **search**: Deshabilitado (false) para selector de opciones fijas
   * 
   * **Optimización:**
   * - Memoizado con useMemo para evitar recreación en cada render
   * - Dependencias específicas para recálculo solo cuando es necesario
   * - Previene re-renders innecesarios del componente hijo DropdownMC
   * - Mejora performance en formularios con muchos selectores
   * 
   * **Beneficio de Performance:**
   * - DropdownMC recibe el mismo objeto de referencia entre renders
   * - Solo se recalcula cuando las dependencias realmente cambian
   * - Especialmente útil en formularios complejos con validaciones
   * 
   * **Dependencias para Recalculo:**
   * - name, label, defaultValue, onSelect, placeholder
   * - className, useFloatingLabel
   * - renderTypeOption, renderSelectedType (funciones memoizadas)
   * 
   * @type {Object} Objeto con propiedades para DropdownMC
   * 
   * @example
   * ```javascript
   * // El objeto se recrea solo cuando cambian las dependencias:
   * // props del componente o funciones de renderizado memoizadas
   * ```
   */
  const dropdownProps = useMemo(() => ({
    id: "template-type-select",
    name,
    label: defaultLabel,
    options: messageTypeOptions,
    defaultValue,
    onSelect,
    placeholder: defaultPlaceholder,
    className,
    useFloatingLabel,
    renderOption: renderTypeOption,
    renderSelected: renderSelectedType,
    search: false
  }), [
    name,
    defaultLabel,
    messageTypeOptions,
    defaultValue,
    onSelect,
    defaultPlaceholder,
    className,
    useFloatingLabel,
    renderTypeOption,
    renderSelectedType
  ]);

  return <DropdownMC {...dropdownProps} />;
});

WsTemplateTypeSelect.displayName = 'WsTemplateTypeSelect';

export default WsTemplateTypeSelect;
